
import React, { useCallback, useEffect, useRef } from "react";
import embed from "vega-embed";

import ButtonSimple from "@/components/ButtonSimple";
import TooltipCustom from "@/components/TooltipCustom";
import { IconAiOutlineDownload } from "@/utils/icons";
import html2canvas from "html2canvas";
import { lineBreakerForChartTitle } from "@/utils";

const ChartBar = (props: any) => {

  const { item } = props;
  const chartData = item?.data;
  const legendTitle = item?.legend_title || '';
  const uniqListOfX = [...new Set(chartData?.map((item: any) => item?.category || ''))];
  const chartRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const viewRef = useRef<any>(null);
  const customChartRef = useRef<HTMLDivElement>(null);
  const handleExport = useCallback(() => {
    if (viewRef.current) {
      viewRef.current.toImageURL('png', 2).then((url: string) => {
        const link = document.createElement('a');
        link.download = `${item?.title || 'chart'}.png`;
        link.href = url;
        link.click();
      });
    }
  }, [item?.title]);

  const renderChart = useCallback(() => {

    if (!chartRef.current || !chartData) {
      return;
    }

    const chartWidth = chartRef.current.offsetWidth > 350 ? chartRef.current.offsetWidth : 350;
    const title = item?.title || "";
    const formattedTitle = lineBreakerForChartTitle(title, chartWidth);

    const spec: any = {
      "$schema": "https://vega.github.io/schema/vega/v5.json",
      "description": item?.description || "",
      "width": chartWidth,
      "height": 400,
      // "padding": 0,
      "padding": { "top": 0, "left": 0, "bottom": 0, "right": 10 },
      "autosize": { "type": "fit", "contains": "padding" },
      "config": {
        "title": { "font": "Arial", "fontSize": 10 },
        "text": { "font": "Arial", "fontSize": 10 },
        "axis": { "labelFont": "Arial", "labelFontSize": 10, "labelColor": "black" }
      },
      "title": {
        "text": formattedTitle,
        "fontSize": 12,
        "fontFamily": "Arial",
        "color": "black",
        "anchor": "middle",
        "lineHeight": 14
      },
      "data": [
        {
          "name": "table",
          "values": chartData
        }
      ],
      "signals": [
        {
          "name": "tooltip",
          "value": {},
          "on": [
            { "events": "rect:pointerover", "update": "datum" },
            { "events": "rect:pointerout", "update": "{}" }
          ]
        }
      ],
      "scales": [
        {
          "name": "xscale",
          "type": "band",
          "domain": { "data": "table", "field": "category" },
          "range": "width",
          "padding": 0.3,
          "round": true
        },
        {
          "name": "yscale",
          "domain": { "data": "table", "field": "data_val" },
          "nice": true,
          "range": "height"
        },
        {
          "name": "color",
          "type": "ordinal",
          "domain": { "data": "table", "field": "category" },
          "range": { "data": "table", "field": "colorParam" }
        }
      ],
      "axes": [
        {
          "orient": "bottom",
          "scale": "xscale",
          "labelFontSize": 0,
          "labelColor": "red"
        },
        {
          "orient": "left",
          "scale": "yscale",
          "tickCount": 5
        }
      ],
      "marks": [
        {
          "type": "rect",
          "from": { "data": "table" },
          "encode": {
            "enter": {
              "x": { "scale": "xscale", "field": "category" },
              "width": { "scale": "xscale", "band": 1 },
              "y": { "scale": "yscale", "field": "data_val" },
              "y2": { "scale": "yscale", "value": 0 }
            },
            "update": {
              "fill": { "scale": "color", "field": "category" },
            },
          },
          "tooltip": { "signal": "tooltip" }
        },
        {
          "type": "text",
          "from": { "data": "table" },
          "encode": {
            "update": {
              "x": { "scale": "xscale", "field": "category", "band": 0.5 },
              "y": { "scale": "yscale", "field": "data_val", "offset": -8 },
              "align": { "value": "center" },
              "baseline": { "value": "middle" },
              "fill": { "value": "#000000" },
              "text": { "field": "data_val" },
              "fontSize": { "value": 12 }
            }
          }
        },
        {
          "type": "text",
          "from": { "data": "table" },
          "encode": {
            "enter": {
              "x": { "scale": "xscale", "field": "category", "band": 0.5 },
              "y": { "signal": "height + 15" },
              "align": { "value": "center" },
              "baseline": { "value": "bottom" },
              "text": { "field": "category" },
              "fontSize": { "value": 9 },
              "fill": { "value": "black" },
              "limit": { "signal": "bandwidth('xscale')" }
            }
          }
        }
      ]
    }


    if (legendTitle) {
      Object.assign(spec, {
        "legends": [
          {
            "fill": "color",
            "title": legendTitle,
            "orient": "right",
            "titleFont": "Arial",
            "padding": 0,
            "titleFontSize": 12,
            "labelFont": "Arial",
            "labelFontSize": 12
          }
        ],
      })
    }

    embed(chartRef.current as unknown as HTMLElement, spec, {
      actions: {
        export: true, // enable export menu
        source: false, // hide "View Source"
        compiled: false, // hide "View Compiled"
        editor: false // hide "Open in Vega Editor"
      }
    }).then((vegaEmbedResult: any) => {
      viewRef.current = vegaEmbedResult.view;
      const menu = chartRef.current?.querySelector(".vega-actions");
      if (menu) {
        const svgButton = menu.querySelector('a[title="Save as SVG"]');
        if (svgButton) {
          svgButton.remove();
        }
      }
    });

  }, [item]);

  // Debounced resize handler
  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      renderChart();
    }, 300);
  }, [renderChart]);

  useEffect(() => {

    renderChart();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [renderChart, handleResize]);

  const exportAsPNG = async () => {
    if (customChartRef.current) {
      const canvas = await html2canvas(customChartRef.current, { scale: 2 });
      const link = document.createElement('a');
      link.download = `${item?.title || 'table'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
  };

  return (
    <div>
      <TooltipCustom title="Export as PNG">
        <ButtonSimple
          // onClick={handleExport}
          onClick={exportAsPNG}
          className='flex justify-self-end mb-1'
        >
          <IconAiOutlineDownload />
        </ButtonSimple>
      </TooltipCustom>
      <div ref={customChartRef}>
        <div
          id="bar-chart"
          className="!p-0 !pr-0 !pe-0 "
          ref={chartRef}
        />
        <div className="pb-2 pl-1">
          {uniqListOfX.map((item: any, index: number) => (
            <div key={item} className="mt-1 pb-[2px] leading-[7px] text-[10px] text-[#000000] ">
              <b>{index + 1}.</b> {item}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


export default ChartBar;